import React, { useMemo, useState } from 'react';
import { Chip, styled, Typography } from '@mui/material';
import { format, isValid } from 'date-fns';
import { getGuideStatusLabel } from 'helpers/guideStatus';
import { moneyFormat } from 'helpers/moneyFormat';
import history from 'services/history';
import { Guide } from 'types/guide';
import { Surgerie } from 'types/surgerie';

type Filter = 'all' | 'budget' | 'guide';

interface TimelineEvent {
  key: string;
  kind: 'budget' | 'guide';
  time: number;
  date: string;
  badge: string;
  title: string;
  subtitle: string;
  value: string;
  note?: string;
  status: string;
  statusLabel: string;
  url: string;
}

const Section = styled('section')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1.5, 2),
}));

const Filters = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.75),
  flexWrap: 'wrap',
}));

const Event = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '92px minmax(0, 1fr) auto',
  columnGap: theme.spacing(1.5),
  alignItems: 'center',
  padding: theme.spacing(1.25, 0),
  cursor: 'pointer',
  '&:not(:last-of-type)': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  '&:hover': {
    backgroundColor: theme.palette.grey[50],
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    '& .date': { gridColumn: '1 / -1' },
  },
}));

const Badge = styled('span')(({ theme }) => ({
  display: 'inline-block',
  marginRight: theme.spacing(0.75),
  padding: '1px 7px',
  borderRadius: 6,
  fontSize: 11,
  fontWeight: 700,
  '&.budget': { backgroundColor: '#fff8e1', color: '#8a6100' },
  '&.guide': { backgroundColor: '#e8eef9', color: '#1c4a8a' },
}));

const Status = styled('span')(({ theme }) => ({
  display: 'inline-block',
  marginTop: 2,
  padding: '2px 9px',
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 600,
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.text.secondary,
  '&.scheduled, &.open': { backgroundColor: '#fff8e1', color: '#8a6100' },
  '&.completed, &.closed': { backgroundColor: '#e8f5ec', color: '#1e6b35' },
  '&.no_show': { backgroundColor: '#fff0e0', color: '#9a4b00' },
  '&.deleted': { backgroundColor: '#fdecec', color: '#a32020' },
}));

const budgetStatusLabel: Record<string, string> = { open: 'Aberto', closed: 'Fechado', deleted: 'Cancelado' };

function toTime(value: string | Date | null | undefined) {
  if (!value) return 0;

  const date = new Date(typeof value === 'string' ? value.replace(' ', 'T') : value);
  return isValid(date) ? date.getTime() : 0;
}

function formatDay(time: number) {
  return time ? format(new Date(time), 'dd/MM/yyyy') : '-';
}

// Quem aparece ao lado do procedimento: na ficha do paciente é o médico; na do médico, o paciente.
type Counterpart = 'doctor' | 'patient';

function buildEvents(budgetItems: Surgerie[], guideItems: Guide[], counterpart: Counterpart): TimelineEvent[] {
  const budgets: TimelineEvent[] = budgetItems.map(budget => {
    const time = toTime(budget.created_at);

    return {
      key: `budget-${budget.id_int}`,
      kind: 'budget',
      time,
      date: formatDay(time),
      badge: `Orçamento nº ${budget.id_int}`,
      title: budget.procedure?.name || '-',
      subtitle:
        [counterpart === 'patient' ? budget.patient?.name : undefined, budget.hospital?.company_name]
          .filter(Boolean)
          .join(' · ') || '-',
      value: moneyFormat(budget.value_total),
      note: budget.value_paid ? `Pago ${moneyFormat(budget.value_paid)}` : undefined,
      status: budget.status,
      statusLabel: budgetStatusLabel[budget.status] || budget.status,
      url: `/surgeries/${budget.status === 'closed' ? 'closed' : 'open'}/${budget.id_int}/print`,
    };
  });

  const guides: TimelineEvent[] = guideItems.map(guide => {
    const time = toTime(guide.appointment_date);
    const payments = (guide.payment_methods || [])
      .filter(item => item.active)
      .map(item => item.payment_method?.name)
      .filter(Boolean)
      .join(', ');

    return {
      key: `guide-${guide.id}`,
      kind: 'guide',
      time,
      date: formatDay(time),
      badge: 'Guia',
      title: guide.procedure?.name || '-',
      subtitle:
        [counterpart === 'patient' ? guide.patient?.name : guide.doctor?.name, payments].filter(Boolean).join(' · ') ||
        '-',
      value: moneyFormat(guide.value_total),
      status: guide.status,
      statusLabel: getGuideStatusLabel(guide.status),
      url: `/guides/${guide.id}/print`,
    };
  });

  return [...budgets, ...guides].sort((a, b) => b.time - a.time);
}

interface RecordTimelineProps {
  budgets: Surgerie[];
  guides: Guide[];
  counterpart: Counterpart;
  // "Este paciente" / "Este médico"
  subject: string;
  // sem guias (ex.: hospital) os filtros por tipo não fazem sentido
  showFilters?: boolean;
}

// Guias e orçamentos do registro, do mais recente para o mais antigo.
const RecordTimeline: React.FC<RecordTimelineProps> = ({
  budgets,
  guides,
  counterpart,
  subject,
  showFilters = true,
}) => {
  const [filter, setFilter] = useState<Filter>('all');
  const events = useMemo(() => buildEvents(budgets, guides, counterpart), [budgets, guides, counterpart]);
  const emptyMessage: Record<Filter, string> = {
    all: `${subject} ainda não tem guias nem orçamentos.`,
    budget: `${subject} ainda não tem orçamentos cirúrgicos.`,
    guide: `${subject} ainda não tem guias.`,
  };
  const visible = events.filter(event => filter === 'all' || event.kind === filter);

  const filters: Array<{ id: Filter; label: string; count: number }> = [
    { id: 'all', label: 'Tudo', count: events.length },
    { id: 'budget', label: 'Orçamentos', count: budgets.length },
    { id: 'guide', label: 'Guias', count: guides.length },
  ];

  return (
    <Section>
      {showFilters && (
        <Filters>
          {filters.map(item => (
            <Chip
              key={item.id}
              size="small"
              clickable
              color={filter === item.id ? 'primary' : 'default'}
              variant={filter === item.id ? 'filled' : 'outlined'}
              label={`${item.label} ${item.count}`}
              onClick={() => setFilter(item.id)}
            />
          ))}
        </Filters>
      )}

      {visible.length === 0 ? (
        <Typography variant="body2" color="textSecondary" sx={{ py: 3, textAlign: 'center' }}>
          {emptyMessage[filter]}
        </Typography>
      ) : (
        <div>
          {visible.map(event => (
            <Event key={event.key} onClick={() => history.push(event.url, { returnTo: history.location.pathname })}>
              <Typography className="date" variant="caption" color="textSecondary">
                {event.date}
              </Typography>
              <div style={{ minWidth: 0 }}>
                <Typography variant="body2" component="div">
                  <Badge className={event.kind}>{event.badge}</Badge>
                  <strong>{event.title}</strong>
                </Typography>
                <Typography variant="caption" color="textSecondary" noWrap display="block">
                  {event.subtitle}
                </Typography>
              </div>
              <div style={{ textAlign: 'right' }}>
                <Typography variant="body2">{event.value}</Typography>
                {event.note && (
                  <Typography variant="caption" color="textSecondary" display="block">
                    {event.note}
                  </Typography>
                )}
                <Status className={event.status}>{event.statusLabel}</Status>
              </div>
            </Event>
          ))}
        </div>
      )}
    </Section>
  );
};

export default RecordTimeline;
